import React, { useState, useEffect } from 'react';
import { SR15_PATHWAYS, SR15Pathway } from '../../data/sr15Data';
import { SPM_FIGURE_METADATA } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Play, Pause, Waves, Sparkles, Filter } from 'lucide-react';

interface SR15SPM2PathwaysProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

export const SR15SPM2Pathways: React.FC<SR15SPM2PathwaysProps> = ({ isPlaying, onTogglePlay }) => {
  const meta = SPM_FIGURE_METADATA['sr15_spm2'];
  const audioEngine = AudioEngine.getInstance();

  const [selectedPathwayId, setSelectedPathwayId] = useState<string>('p1');
  const [timeIndex, setTimeIndex] = useState<number>(0);

  const activePathway = SR15_PATHWAYS.find((p) => p.id === selectedPathwayId) || SR15_PATHWAYS[0];
  const years = activePathway.trajectory.map((t) => t.year);

  // Auto-play timeline loop
  useEffect(() => {
    let timer: number;
    if (isPlaying) {
      timer = window.setInterval(() => {
        setTimeIndex((prev) => (prev + 1) % years.length);
      }, 900);
    } else {
      audioEngine.stopAll();
    }
    return () => clearInterval(timer);
  }, [isPlaying, years.length]);

  // Audio Update per step
  useEffect(() => {
    if (!audioEngine.getIsUnlocked() || !isPlaying) return;
    const pt = activePathway.trajectory[timeIndex];
    if (!pt) return;

    if (pt.netCO2 > 0) {
      // Positive Net CO2: Anthropo-Pulse FM synth
      const freq = audioEngine.tempToFreq(pt.netCO2 / 20);
      const fmIndex = Math.max(1, (pt.netCO2 / 40) * 8);
      const gain = Math.min(0.4, (pt.netCO2 / 40) * 0.4);

      audioEngine.startAnthropoPulse(freq, fmIndex, gain);
      audioEngine.stopCDRSweep();
    } else if (pt.netCO2 === 0) {
      // Net Zero: Silence
      audioEngine.stopAnthropoPulse();
      audioEngine.stopCDRSweep();
    } else {
      // Negative Emissions (CDR/BECCS): Inverted Sub-Bass Sweep
      audioEngine.stopAnthropoPulse();
      audioEngine.startCDRSweep(Math.abs(pt.cdrVolume));
    }
  }, [timeIndex, selectedPathwayId, isPlaying]);

  const activePt = activePathway.trajectory[timeIndex];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>

        <button
          onClick={onTogglePlay}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-lg ${
            isPlaying ? 'bg-amber-500 text-slate-950 glow-amber' : 'bg-cyan-500 text-slate-950 glow-cyan'
          }`}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          <span>{isPlaying ? 'Pause Pathway' : 'Play Pathway Trajectory'}</span>
        </button>
      </div>

      {/* Main Pathway Module */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-6">
        {/* Pathway Archetype Selector */}
        <div className="space-y-2">
          <span className="text-xs font-mono text-slate-400 block font-semibold">Select 1.5°C Archetype Pathway:</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {SR15_PATHWAYS.map((p) => {
              const isSelected = selectedPathwayId === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => {
                    setSelectedPathwayId(p.id);
                    setTimeIndex(0);
                  }}
                  className={`p-3.5 rounded-2xl border text-left font-sans text-xs transition-all ${
                    isSelected
                      ? 'bg-slate-800 border-cyan-400 text-white glow-cyan shadow-md'
                      : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }}></span>
                    <span className="font-bold text-slate-100">{p.shortName}</span>
                  </div>
                  <p className="text-[10px] text-slate-400 leading-tight line-clamp-2">{p.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Year Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
          <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1">
            <span className="text-xs font-mono text-cyan-400 font-semibold block">Net CO2 Emissions</span>
            <span className="text-2xl font-extrabold font-mono text-white">
              {activePt.netCO2 > 0 ? '+' : ''}{activePt.netCO2} <span className="text-xs text-slate-400 font-sans">GtCO2/yr</span>
            </span>
            <span className="text-[10px] text-slate-500 block">
              {activePt.netCO2 === 0 ? 'Net Zero Achieved (Silence)' : activePt.netCO2 < 0 ? 'Net Negative Emissions' : 'Positive Emissions'}
            </span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1">
            <span className="text-xs font-mono text-emerald-400 font-semibold block">CDR / BECCS Volume</span>
            <span className="text-2xl font-extrabold font-mono text-emerald-300">
              {activePt.cdrVolume} <span className="text-xs text-slate-400 font-sans">GtCO2/yr</span>
            </span>
            <span className="text-[10px] text-slate-500 block">Inverted Sub-Bass Drone Sweep</span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1">
            <span className="text-xs font-mono text-amber-400 font-semibold block">Non-CO2 Radiative Forcing</span>
            <span className="text-2xl font-extrabold font-mono text-amber-300">
              +{activePt.nonCO2Forcing.toFixed(2)} <span className="text-xs text-slate-400 font-sans">W/m²</span>
            </span>
            <span className="text-[10px] text-slate-500 block">Methane & Black Carbon</span>
          </div>
        </div>

        {/* Timeline Scrubber */}
        <div className="space-y-2 pt-2">
          <div className="flex justify-between text-xs font-mono text-slate-400">
            <span>Year 2010</span>
            <span className="text-cyan-300 font-bold">Selected Year: {activePt.year}</span>
            <span>Year 2100</span>
          </div>
          <input
            type="range"
            min="0"
            max={years.length - 1}
            value={timeIndex}
            onChange={(e) => setTimeIndex(parseInt(e.target.value))}
            className="w-full h-2 rounded-lg bg-slate-800 accent-cyan-400 cursor-pointer"
          />
        </div>
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
